/**
 * Promo Code API Service
 * Uses the NEW promotions API endpoints
 */

import { isValidDateString } from '@/utils/dateUtils';

// Goes through the server-side proxy (src/app/api/proxy/[...path]/route.js)
// which injects the Basic credential — never sent from the client.
const API_BASE_URL = '/api/proxy';

// Add required headers for API requests
const getApiHeaders = () => {
  // Try to get property code from localStorage
  let database = 'SWH'; // Default property code
  
  if (typeof window !== 'undefined') {
    try {
      const savedProperty = localStorage.getItem('selectedProperty');
      if (savedProperty) {
        const property = JSON.parse(savedProperty);
        if (property && property.code) {
          database = property.code.split(',')[0].trim();
          console.log('Using property code from localStorage for promo API:', database);
        }
      }
    } catch (e) {
      console.warn('Error reading selected property for promo API:', e.message);
    }
  }

  const headers = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Database': database,
  };
  console.log('Using promo API headers:', headers);
  return headers;
};

/**
 * Validate promo code using the NEW promotions API
 * Endpoint: GET /api/promotions/validate/:code
 * @param {string} code - The promo code to validate
 * @returns {Promise<Object>} Validation result with code details
 */
export const validatePromoCode = async (code) => {
  try {
    if (!code || code.trim() === '') {
      return {
        success: false,
        message: 'Promo code is required',
        data: null
      };
    }

    console.log('🔍 Validating promo code:', code);

    // Use the NEW promotions validate endpoint
    const response = await fetch(`${API_BASE_URL}/promotions/validate/${code}`, {
      method: 'GET',
      headers: getApiHeaders(),
      redirect: 'follow'
    });

    if (!response.ok) {
      // If the endpoint returns 404 or other error, the promo code doesn't exist
      return {
        success: false,
        message: 'Promo code not found. Please check the code and try again.',
        data: null
      };
    }

    const result = await response.json();
    console.log('📋 Promo code validation result:', result);

    // Handle new API response format
    if (result.status === 'success') {
      // Success response - promo code is valid and can be used
      const promoData = result.data || result;
      return {
        success: true,
        message: 'Promo code is valid and ready to use',
        data: {
          ...promoData,
          is_valid: true,
          remaining_uses: Math.max(0, (promoData.usage_limit || promoData.max_use || 999) - (promoData.total_usage || promoData.current_usage || 0))
        }
      };
    } else if (result.status === 'error') {
      // Handle specific error cases with detailed messages
      let errorMessage = result.message || 'Invalid promo code';
      
      // Check for specific error types based on message content
      if (result.message && result.message.toLowerCase().includes('usage limit exceeded')) {
        errorMessage = `This promo code has reached its usage limit`;
      } else if (result.message && result.message.toLowerCase().includes('expired')) {
        errorMessage = `This promo code has expired`;
      } else if (result.message && result.message.toLowerCase().includes('not found')) {
        errorMessage = 'Promo code not found. Please check the code and try again.';
      } else if (result.message && result.message.toLowerCase().includes('not yet active')) {
        errorMessage = 'This promo code is not yet active. Please check the valid dates.';
      }
      
      return {
        success: false,
        message: errorMessage,
        data: null,
        details: result.details || null
      };
    }

    // Fallback for unexpected response format
    return {
      success: false,
      message: 'Unexpected response format from server',
      data: null
    };
  } catch (error) {
    console.error('Error validating promo code:', error);
    return {
      success: false,
      message: error.message || 'Failed to validate promo code. Please check your connection and try again.',
      data: null
    };
  }
};

/**
 * Validate referral code using the referral codes API
 * Endpoint: GET /api/promo-reffcodes/code/:code
 * Returns HTTP 200 with "status": "error" for inactive/expired/limit-exceeded codes,
 * and HTTP 404 when the code does not exist at all.
 * @param {string} code - The referral code to validate
 * @returns {Promise<Object>} Validation result with code details
 */
export const validateReferralCode = async (code) => {
  try {
    if (!code || code.trim() === '') {
      return {
        success: false,
        message: 'Referral code is required',
        data: null
      };
    }

    console.log('🔍 Validating referral code:', code);

    const response = await fetch(`${API_BASE_URL}/promo-reffcodes/code/${encodeURIComponent(code.trim())}`, {
      method: 'GET',
      headers: getApiHeaders(),
      redirect: 'follow'
    });

    // Code does not exist at all
    if (response.status === 404) {
      return {
        success: false,
        message: 'Referral code not found. Please check the code and try again.',
        errorType: 'NOT_FOUND',
        data: null
      };
    }

    if (!response.ok) {
      return {
        success: false,
        message: 'Failed to validate referral code. Please try again.',
        data: null
      };
    }

    const result = await response.json();
    console.log('📋 Referral code validation result:', result);

    if (result.status === 'success' && result.data) {
      // Success response - referral code is valid and ready to use
      const codeData = result.data;
      const promoData = {
        ...codeData,
        referral_code: codeData.referral_code || codeData.code || code.trim(),
        promo_code: codeData.referral_code || codeData.code || code.trim(),
        code: codeData.referral_code || codeData.code || code.trim(),
        discount_type: codeData.benefit,
        discount_value: codeData.benefit_amount,
        benefit: codeData.benefit,
        benefit_amount: codeData.benefit_amount,
        benefit_description: codeData.benefit_description || codeData.itemdesc || '',
        description: codeData.benefit_description || codeData.itemdesc || '',
        usage_limit: codeData.max_use || 0,
        total_usage: codeData.current_usage || 0,
        is_valid: true,
        remaining_uses: Math.max(0, (codeData.max_use || 0) - (codeData.current_usage || 0))
      };
      return {
        success: true,
        message: 'Referral code is valid and ready to use',
        data: promoData
      };
    } else if (result.status === 'error') {
      // Handle specific error cases with detailed messages
      let errorMessage = result.message || 'Invalid referral code';
      const messageLower = String(result.message || '').toLowerCase();

      if (messageLower.includes('not yet active')) {
        errorMessage = 'Referral code is not yet active. Please check the valid dates.';
      } else if (messageLower.includes('expired')) {
        errorMessage = 'Referral code has expired.';
      } else if (messageLower.includes('usage limit exceeded')) {
        errorMessage = 'Referral code usage limit exceeded.';
      } else if (messageLower.includes('not found')) {
        errorMessage = 'Referral code not found. Please check the code and try again.';
      }

      return {
        success: false,
        message: errorMessage,
        data: null,
        details: result.details || null
      };
    }

    // Fallback for unexpected response format
    return {
      success: false,
      message: 'Unexpected response format from server',
      data: null
    };
  } catch (error) {
    console.error('Error validating referral code:', error);
    return {
      success: false,
      message: error.message || 'Failed to validate referral code. Please check your connection and try again.',
      data: null
    };
  }
};

/**
 * Record referral code usage for a reservation
 * Endpoint: POST /api/promo-reffcodes/use/:code
 * @param {string} code - The referral code that was used
 * @param {Object} usageData - Usage data
 * @param {string} usageData.reservation_id - The reservation ID (required)
 * @param {string} usageData.guest_name - The guest name (required)
 * @param {string} usageData.guest_phone - The guest phone number (required)
 * @param {string} [usageData.source] - Usage source (default: 'web')
 * @returns {Promise<Object>} API response
 */
export const useReferralCode = async (code, usageData = {}) => {
  try {
    if (!code || !code.trim()) {
      return { success: false, message: 'Referral code is required' };
    }
    if (!usageData.reservation_id) {
      return { success: false, message: 'reservation_id is required' };
    }

    console.log('📋 Recording referral code usage:', { code, usageData });

    const response = await fetch(`${API_BASE_URL}/promo-reffcodes/use/${encodeURIComponent(code.trim())}`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        reservation_id: usageData.reservation_id,
        guest_name: usageData.guest_name || '',
        guest_phone: usageData.guest_phone || '',
        source: usageData.source || 'web'
      })
    });

    const result = await response.json();
    console.log('✅ Referral code usage response:', result);

    return {
      success: result.status === 'success',
      message: result.message || (result.status === 'success' ? 'Referral code used successfully' : 'Failed to record referral code usage'),
      data: result.data || null
    };
  } catch (error) {
    console.error('❌ Error recording referral code usage:', error);
    return {
      success: false,
      message: error.message || 'Failed to record referral code usage'
    };
  }
};

/**
 * Record promotion usage after successful reservation
 * This should be called after a reservation is created with a promo code
 * Uses the NEW promotions API endpoint: POST /api/promotions/usage
 * @param {Object} usageData - Usage data
 * @param {string} usageData.promo_code - The promotion code (required)
 * @param {string} usageData.booking_id - The reservation/booking ID (required)
 * @param {string} usageData.guest_id - The guest/member ID (optional)
 * @param {number} usageData.discount_amount - The discount amount applied (optional)
 * @param {string} usageData.member_id - The membership ID from dashboard membership (optional)
 * @returns {Promise<Object>} API response
 */
export const recordPromotionUsage = async (usageData) => {
  try {
    console.log('📋 Recording promotion usage:', usageData);

    if (!usageData.promo_code || !usageData.booking_id) {
      console.warn('⚠️ Missing required fields for promotion usage:', {
        promo_code: !!usageData.promo_code,
        booking_id: !!usageData.booking_id
      });
      return { success: false, message: 'promo_code and booking_id are required' };
    }

    const response = await fetch(`${API_BASE_URL}/promotions/usage`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify({
        promo_code: usageData.promo_code,
        booking_id: usageData.booking_id,
        guest_id: usageData.guest_id || null,
        discount_amount: usageData.discount_amount || 0,
        member_id: usageData.member_id || null
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to record promotion usage:', errorText);
      return {
        success: false,
        message: `Failed to record promotion usage: ${response.status}`
      };
    }

    const result = await response.json();
    console.log('✅ Promotion usage recorded:', result);
    
    return {
      success: result.status === 'success',
      message: result.message || 'Promotion usage recorded',
      data: result.data
    };
  } catch (error) {
    console.error('❌ Error recording promotion usage:', error);
    return {
      success: false,
      message: error.message
    };
  }
};

/**
 * Get guest/member promotion usage history for a specific promotion
 * Uses GET /api/promotions/:promotionId/usage to get all usage records for this promo,
 * then filters client-side by guest identifier (email / member_code).
 * Falls back to the generic usages endpoint if no promotionId is provided.
 * @param {string} guestId - The guest ID, member code or email
 * @param {number} [promotionId] - Optional promotion ID to scope the query
 * @returns {Promise<Object>} API response with usage history (filtered for this guest)
 */
export const getGuestPromoUsageHistory = async (guestId, promotionId = null) => {
  try {
    if (!guestId) {
      return {
        success: false,
        message: 'Guest ID is required',
        data: []
      };
    }

    let result;

    if (promotionId) {
      // Preferred path: use the documented GET /api/promotions/:id/usage endpoint
      console.log('📋 Fetching usage history for promotion', promotionId, 'guest:', guestId);
      const response = await fetch(`${API_BASE_URL}/promotions/${promotionId}/usage`, {
        method: 'GET',
        headers: getApiHeaders(),
        redirect: 'follow'
      });

      if (!response.ok) {
        console.warn('⚠️ Promotions usage endpoint returned:', response.status);
        return { success: true, data: [] };
      }

      result = await response.json();
    } else {
      // Fallback: use generic usages endpoint with guest_id filter
      console.log('📋 Fetching promo usage history for guest (fallback):', guestId);
      const response = await fetch(`${API_BASE_URL}/promotions/usages?guest_id=${encodeURIComponent(guestId)}&limit=100`, {
        method: 'GET',
        headers: getApiHeaders(),
        redirect: 'follow'
      });

      if (!response.ok) {
        console.warn('⚠️ Promotions usages endpoint returned:', response.status);
        return { success: true, data: [] };
      }

      result = await response.json();
    }

    console.log('📋 Guest promo usage history raw result:', result);

    if (result.status === 'success' && Array.isArray(result.data)) {
      // Normalize guestId for flexible matching (lowercase email, member_code)
      const normalizedGuestId = typeof guestId === 'string' ? guestId.toLowerCase().trim() : guestId;

      // Filter usage records to only those belonging to this guest
      // Match against guest_id (stored member_code or numeric ID) and also via member email
      const guestUsage = result.data.filter(item => {
        const itemGuestId = item.guest_id;
        const itemMemberId = item.member_id;

        if (!itemGuestId && !itemMemberId) return false;

        // Direct match on guest_id
        if (itemGuestId && String(itemGuestId).toLowerCase().trim() === normalizedGuestId) {
          return true;
        }

        // Match on member_id (some backends store email here)
        if (itemMemberId && String(itemMemberId).toLowerCase().trim() === normalizedGuestId) {
          return true;
        }

        // If guestId looks like an email, try matching numeric member_id
        // (backend may resolve email to numeric ID when storing guest_id)
        return false;
      });

      console.log(`📋 Filtered usage for guest "${guestId}" on promotion ${promotionId}: ${guestUsage.length} records`, guestUsage);

      return {
        success: true,
        data: guestUsage
      };
    }

    return {
      success: true,
      data: []
    };
  } catch (error) {
    console.error('Error fetching guest promo usage history:', error);
    return {
      success: true,
      data: []
    };
  }
};

/**
 * Check if a guest can use a specific promo code based on usage_per_guest limit.
 * Uses GET /api/promotions/:promotionId/usage and filters by guest identifier
 * (email or member_code) to count how many times this guest has used the promo.
 * @param {string} guestId - The guest ID, member code or email
 * @param {Object} promoData - The promo code data with usage_per_guest and id fields
 * @returns {Promise<Object>} Validation result with usage info
 */
export const checkGuestPromoUsage = async (guestId, promoData) => {
  try {
    // If no usage_per_guest limit set, guest can always use the promo
    if (!promoData || !promoData.usage_per_guest || promoData.usage_per_guest <= 0) {
      return {
        canUse: true,
        currentUsage: 0,
        maxUsage: 0,
        remainingUsage: Infinity,
        message: 'No usage limit per guest'
      };
    }

    // If no guest ID (not logged in), can't track individual usage
    if (!guestId) {
      return {
        canUse: true,
        currentUsage: 0,
        maxUsage: promoData.usage_per_guest,
        remainingUsage: promoData.usage_per_guest,
        message: 'Guest not logged in - usage tracking unavailable'
      };
    }

    const promoId = promoData.id;
    const promoCode = promoData.promo_code || promoData.referral_code;

    // Fetch usage history scoped to this specific promotion
    // The returned data is already filtered client-side by guestId in getGuestPromoUsageHistory
    const historyResult = await getGuestPromoUsageHistory(guestId, promoId);

    if (!historyResult.success) {
      console.warn('⚠️ Could not verify guest promo usage history, allowing usage');
      return {
        canUse: true,
        currentUsage: 0,
        maxUsage: promoData.usage_per_guest,
        remainingUsage: promoData.usage_per_guest,
        message: 'Could not verify usage history - usage allowed'
      };
    }

    const usageCount = historyResult.data ? historyResult.data.length : 0;
    const maxUsage = promoData.usage_per_guest;
    const remainingUsage = Math.max(0, maxUsage - usageCount);
    const canUse = usageCount < maxUsage;

    console.log(`📊 Guest promo usage check for ${promoCode}:`, {
      guestId,
      promoId,
      usageCount,
      maxUsage,
      remainingUsage,
      canUse
    });

    return {
      canUse,
      currentUsage: usageCount,
      maxUsage,
      remainingUsage,
      message: canUse 
        ? `You have used this promo ${usageCount}/${maxUsage} times. ${remainingUsage} uses remaining.`
        : `You have reached the maximum usage (${maxUsage}) for this promo code.`,
      usageHistory: historyResult.data || []
    };
  } catch (error) {
    console.error('Error checking guest promo usage:', error);
    return {
      canUse: true,
      currentUsage: 0,
      maxUsage: promoData?.usage_per_guest || 0,
      remainingUsage: promoData?.usage_per_guest || 0,
      message: 'Error checking usage - usage allowed'
    };
  }
};

/**
 * Validate promo code with member usage check
 * This is an enhanced version that also checks usage_per_guest for logged-in members
 * @param {string} code - The promo code to validate
 * @param {string} guestId - Optional: The guest/member ID to check usage_per_guest
 * @returns {Promise<Object>} Validation result with code details and usage info
 */
export const validatePromoCodeWithUsage = async (code, guestId = null) => {
  try {
    // First, do the basic promo validation
    const basicValidation = await validatePromoCode(code);

    if (!basicValidation.success || !basicValidation.data) {
      return basicValidation;
    }

    const promoData = basicValidation.data;

    // If no guest ID provided, return basic validation result
    if (!guestId) {
      return {
        ...basicValidation,
        guestUsage: {
          canUse: true,
          currentUsage: 0,
          maxUsage: promoData.usage_per_guest || 0,
          remainingUsage: promoData.usage_per_guest || Infinity,
          message: 'Guest not identified - individual usage limit not checked'
        }
      };
    }

    // Check guest usage for this promo
    const guestUsageCheck = await checkGuestPromoUsage(guestId, promoData);

    // If guest has exceeded their usage limit, return error
    if (!guestUsageCheck.canUse) {
      return {
        success: false,
        message: guestUsageCheck.message,
        data: null,
        guestUsage: guestUsageCheck
      };
    }

    // Return successful validation with usage info
    return {
      ...basicValidation,
      guestUsage: guestUsageCheck
    };
  } catch (error) {
    console.error('Error in validatePromoCodeWithUsage:', error);
    return {
      success: false,
      message: error.message || 'Failed to validate promo code',
      data: null
    };
  }
};

/**
 * Calculate discount amount based on promo code benefit
 * @param {Object} promoData - The promo code data
 * @param {number} totalAmount - The total booking amount
 * @returns {number} Calculated discount amount
 */
export const calculatePromoDiscount = (promoData, totalAmount) => {
  if (!promoData) {
    return 0;
  }

  // Support both old and new field names
  const benefit = (promoData.discount_type || promoData.benefit || '').toLowerCase();
  const amount = parseFloat(promoData.discount_value || promoData.benefit_amount || 0);

  if (!benefit || !amount) {
    return 0;
  }

  switch (benefit) {
    case 'fixed_amount':
    case 'discount_amt':
      // Fixed amount discount
      return Math.min(amount, totalAmount); // Don't exceed total amount
    
    case 'percentage':
    case 'discount_pct':
      // Percentage discount
      return Math.round((totalAmount * amount) / 100);

    case 'point':
      // Points don't provide direct discount, but can be used elsewhere
      return 0;

    case 'item':
      // Free items don't provide direct monetary discount
      return 0;

    default:
      return 0;
  }
};

/**
 * Format promo code for special requests with proper formatting
 * @param {Object} promoData - The promo code data object
 * @returns {string} Formatted promo code note
 */
export const formatPromoForSpecialRequests = (promoData) => {
  if (!promoData) {
    return '';
  }
  
  // Handle different field name structures from API responses
  const code = promoData.promo_code || promoData.referral_code || promoData.code || '';
  const description = promoData.description || promoData.benefit_description || '';
  const benefit = promoData.discount_type || promoData.benefit || '';
  
  if (!code) {
    return '';
  }
  
  let promoNote = `\n--- PROMO CODE ---\nCode: ${code}`;
  
  if (description) {
    promoNote += `\nBenefit: ${description}`;
  }
  
  // For "item" type benefits, automatically add the benefit description to the reservation notes
  // This ensures free items/services are clearly documented in the reservation
  if (benefit && benefit.toLowerCase() === 'item') {
    promoNote += `\nSpecial Note: ${description} - Please prepare this item/service for the guest`;
    console.log(`📝 Adding item benefit to reservation notes: ${description}`);
  }
  
  promoNote += `\n--- END PROMO ---`;
  
  return promoNote;
};

/**
 * Check if promo code is currently valid (date and usage check)
 * @param {Object} promoData - The promo code data
 * @returns {Object} Validation result with details
 */
export const isPromoCodeValid = (promoData) => {
  if (!promoData) {
    return { valid: false, reason: 'Promo code not found' };
  }

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Check booking period (booking_start / booking_end) — when guest makes the booking
  // Compare as local date strings (YYYY-MM-DD) to avoid timezone issues
  const bookingStartValid = isValidDateString(promoData.booking_start);
  const bookingEndValid = isValidDateString(promoData.booking_end);
  if (bookingStartValid || bookingEndValid) {
    if (bookingStartValid && today < promoData.booking_start) {
      return { valid: false, reason: 'Promo code is not yet active' };
    }
    if (bookingEndValid && today > promoData.booking_end) {
      return { valid: false, reason: 'Promo code has expired' };
    }
  }

  // Fallback: check legacy valid_from / valid_to fields
  if (!bookingStartValid && !bookingEndValid) {
    if (promoData.valid_from || promoData.start_date) {
      const validFrom = promoData.valid_from || promoData.start_date;
      if (isValidDateString(validFrom) && today < validFrom) {
        return { valid: false, reason: 'Promo code is not yet active' };
      }
    }
    if (promoData.valid_to || promoData.end_date) {
      const validTo = promoData.valid_to || promoData.end_date;
      if (isValidDateString(validTo) && today > validTo) {
        return { valid: false, reason: 'Promo code has expired' };
      }
    }
  }

  // Check usage limit
  const totalUsage = parseInt(promoData.total_usage || promoData.current_usage) || 0;
  const usageLimit = parseInt(promoData.usage_limit || promoData.max_use) || 0;

  if (usageLimit > 0 && totalUsage >= usageLimit) {
    return { valid: false, reason: 'Promo code usage limit reached' };
  }

  return { valid: true, reason: 'Promo code is valid' };
};

// Export utility functions for backward compatibility with referral code system
export const formatReferralForSpecialRequests = formatPromoForSpecialRequests;

// Export usePromoCode as an alias for recordPromotionUsage (backward compatibility)
export const usePromoCode = recordPromotionUsage;
